import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONSENT_KEY = "kryajet_cookie_consent";

export type CookieConsent = "all" | "essential" | null;

export const useCookieConsent = () => {
  const getConsent = (): CookieConsent => {
    try {
      return (localStorage.getItem(CONSENT_KEY) as CookieConsent) ?? null;
    } catch {
      return null;
    }
  };

  const resetConsent = () => {
    try {
      localStorage.removeItem(CONSENT_KEY);
      window.dispatchEvent(new Event("cookie-consent-reset"));
    } catch {
      // ignore
    }
  };

  return { getConsent, resetConsent };
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { getConsent } = useCookieConsent();

  useEffect(() => {
    const check = () => {
      setVisible(getConsent() === null);
    };
    check();
    window.addEventListener("cookie-consent-reset", check);
    return () => window.removeEventListener("cookie-consent-reset", check);
  }, []);

  const handleChoice = (choice: "all" | "essential") => {
    try {
      localStorage.setItem(CONSENT_KEY, choice);
    } catch {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card shadow-lg">
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col gap-4">
          {/* Main row */}
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Icon + text */}
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                We use strictly necessary cookies to keep you logged in and process payments securely.
                No analytics or advertising cookies are used.{" "}
                <Link to="/cookie-policy" className="underline text-foreground hover:text-primary transition-colors">
                  Cookie Policy
                </Link>
              </p>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleChoice("essential")}
                className="whitespace-nowrap"
              >
                Refuse Non-Essential
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpanded((v) => !v)}
                className="whitespace-nowrap"
              >
                Manage Preferences
                {expanded ? (
                  <ChevronUp className="h-4 w-4 ml-1" />
                ) : (
                  <ChevronDown className="h-4 w-4 ml-1" />
                )}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleChoice("all")}
                className="whitespace-nowrap"
              >
                Accept All
              </Button>
            </div>
          </div>

          {/* Expanded preferences panel */}
          {expanded && (
            <div className="border border-border rounded-md p-4 bg-background text-sm space-y-3">
              <h3 className="font-semibold text-foreground">Cookie Categories</h3>

              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground">Strictly Necessary</p>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    Required for authentication (Supabase) and storing your cookie preference. Cannot be disabled.
                  </p>
                </div>
                <span className="shrink-0 text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                  Always on
                </span>
              </div>

              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-foreground">Functional (Payment)</p>
                  <p className="text-muted-foreground text-xs mt-0.5">
                    Stripe fraud-prevention cookies (<code>__stripe_mid</code>, <code>__stripe_sid</code>).
                    Refusing these will not prevent payments from processing.
                  </p>
                </div>
                <span className="shrink-0 text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full">
                  Optional
                </span>
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => handleChoice("essential")}>
                  Save Essential Only
                </Button>
                <Button variant="default" size="sm" onClick={() => handleChoice("all")}>
                  Accept All
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
